import { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Plus, Settings, Eye, EyeOff, Grip, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';

// Widget types available for the dashboard
export interface DashboardWidget {
  id: string;
  type: string;
  title: string;
  description: string;
  component: React.ComponentType<any>;
  defaultProps?: Record<string, any>;
  category: 'analytics' | 'management' | 'reports' | 'alerts';
  requiredRoles?: string[];
  size: 'small' | 'medium' | 'large';
}

interface DashboardLayout {
  id: string;
  widgets: {
    id: string;
    position: number;
    visible: boolean;
    props?: Record<string, any>;
  }[];
}

interface CustomizableDashboardProps {
  availableWidgets: DashboardWidget[];
  userRoles: string[];
  onLayoutChange?: (layout: DashboardLayout) => void;
}

// Mock widget components for demonstration
const MockStatsWidget = ({ title, value, icon: Icon }: any) => (
  <Card className="p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold">{value}</p>
      </div>
      {Icon && <Icon className="w-8 h-8 text-muted-foreground" />}
    </div>
  </Card>
);

const MockChartWidget = ({ title, data }: any) => (
  <Card className="p-4">
    <h3 className="font-semibold mb-4">{title}</h3>
    <div className="h-32 bg-muted rounded flex items-center justify-center">
      <p className="text-muted-foreground">Chart: {data}</p>
    </div>
  </Card>
);

const MockListWidget = ({ title, items }: any) => (
  <Card className="p-4">
    <h3 className="font-semibold mb-4">{title}</h3>
    <div className="space-y-2">
      {items?.map((item: string, index: number) => (
        <div key={index} className="text-sm p-2 bg-muted rounded">
          {item}
        </div>
      ))}
    </div>
  </Card>
);

export const CustomizableDashboard: React.FC<CustomizableDashboardProps> = ({
  availableWidgets,
  userRoles,
  onLayoutChange
}) => {
  const [layout, setLayout] = useState<DashboardLayout>({
    id: 'default',
    widgets: []
  });
  const [isCustomizing, setIsCustomizing] = useState(false);
  const { toast } = useToast();

  // Load saved layout from localStorage
  useEffect(() => {
    const savedLayout = localStorage.getItem('shiftask-dashboard-layout');
    if (savedLayout) {
      try {
        const parsed = JSON.parse(savedLayout);
        setLayout(parsed);
      } catch (error) {
        console.error('Failed to parse saved layout:', error);
        initializeDefaultLayout();
      }
    } else {
      initializeDefaultLayout();
    }
  }, [availableWidgets]);

  const initializeDefaultLayout = () => {
    // Create default layout with user's permitted widgets
    const defaultWidgets = availableWidgets
      .filter(widget => 
        !widget.requiredRoles || 
        widget.requiredRoles.some(role => userRoles.includes(role))
      )
      .slice(0, 6) // Limit to first 6 widgets
      .map((widget, index) => ({
        id: widget.id,
        position: index,
        visible: true,
        props: widget.defaultProps || {}
      }));

    const defaultLayout = {
      id: 'default',
      widgets: defaultWidgets
    };

    setLayout(defaultLayout);
  };

  const saveLayout = (newLayout: DashboardLayout) => {
    localStorage.setItem('shiftask-dashboard-layout', JSON.stringify(newLayout));
    onLayoutChange?.(newLayout);
    toast({
      title: "Layout Saved",
      description: "Your dashboard layout has been saved successfully."
    });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newWidgets = Array.from(layout.widgets);
    const [reorderedWidget] = newWidgets.splice(result.source.index, 1);
    newWidgets.splice(result.destination.index, 0, reorderedWidget);

    // Update positions
    const updatedWidgets = newWidgets.map((widget, index) => ({
      ...widget,
      position: index
    }));

    const newLayout = {
      ...layout,
      widgets: updatedWidgets
    };

    setLayout(newLayout);
    saveLayout(newLayout);
  };

  const toggleWidgetVisibility = (widgetId: string) => {
    const newLayout = {
      ...layout,
      widgets: layout.widgets.map(widget =>
        widget.id === widgetId
          ? { ...widget, visible: !widget.visible }
          : widget
      )
    };
    setLayout(newLayout);
    saveLayout(newLayout);
  };

  const addWidget = (widgetType: string) => {
    const widget = availableWidgets.find(w => w.id === widgetType);
    if (!widget) return;

    // Check if widget already exists
    if (layout.widgets.some(w => w.id === widgetType)) {
      toast({
        title: "Widget Already Added",
        description: "This widget is already on your dashboard.",
        variant: "destructive"
      });
      return;
    }

    const newWidget = {
      id: widget.id,
      position: layout.widgets.length,
      visible: true,
      props: widget.defaultProps || {}
    };

    const newLayout = {
      ...layout,
      widgets: [...layout.widgets, newWidget]
    };

    setLayout(newLayout);
    saveLayout(newLayout);

    toast({
      title: "Widget Added",
      description: `${widget.title} has been added to your dashboard.`
    });
  };

  const removeWidget = (widgetId: string) => {
    const newLayout = {
      ...layout,
      widgets: layout.widgets
        .filter(widget => widget.id !== widgetId)
        .map((widget, index) => ({ ...widget, position: index }))
    };
    setLayout(newLayout);
    saveLayout(newLayout);
  };

  const resetToDefault = () => {
    initializeDefaultLayout();
    toast({
      title: "Layout Reset",
      description: "Dashboard has been reset to default layout."
    });
  };

  const visibleWidgets = layout.widgets
    .filter(widget => widget.visible)
    .sort((a, b) => a.position - b.position);

  const getWidgetComponent = (widgetId: string) => {
    const widget = availableWidgets.find(w => w.id === widgetId);
    const layoutWidget = layout.widgets.find(w => w.id === widgetId);
    
    if (!widget || !layoutWidget) return null;

    const Component = widget.component;
    return (
      <Component
        key={widget.id}
        {...widget.defaultProps}
        {...layoutWidget.props}
      />
    );
  };

  const getGridClass = (size: string) => {
    switch (size) {
      case 'small':
        return 'col-span-1 md:col-span-1';
      case 'medium':
        return 'col-span-1 md:col-span-2';
      case 'large':
        return 'col-span-1 md:col-span-3';
      default:
        return 'col-span-1 md:col-span-2';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Dashboard</h2>
        <div className="flex items-center gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Widget
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add Dashboard Widget</DialogTitle>
              </DialogHeader>
              <ScrollArea className="max-h-96 pr-4">
                <div className="space-y-4">
                  {Object.entries(
                    availableWidgets
                      .filter(widget => 
                        !widget.requiredRoles || 
                        widget.requiredRoles.some(role => userRoles.includes(role))
                      )
                      .reduce((acc, widget) => {
                        if (!acc[widget.category]) {
                          acc[widget.category] = [];
                        }
                        acc[widget.category].push(widget);
                        return acc;
                      }, {} as Record<string, DashboardWidget[]>)
                  ).map(([category, widgets]) => (
                    <div key={category}>
                      <h3 className="font-semibold mb-2 capitalize">{category}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {widgets.map(widget => {
                          const isAdded = layout.widgets.some(w => w.id === widget.id);
                          return (
                            <Card key={widget.id} className="p-3">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-medium">{widget.title}</h4>
                                    <Badge variant="secondary" className="text-xs">
                                      {widget.size}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {widget.description}
                                  </p>
                                </div>
                                <Button
                                  size="sm"
                                  variant={isAdded ? "secondary" : "default"}
                                  onClick={() => addWidget(widget.id)}
                                  disabled={isAdded}
                                >
                                  {isAdded ? "Added" : "Add"}
                                </Button>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                      {category !== 'alerts' && <Separator className="mt-4" />}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsCustomizing(!isCustomizing)}
          >
            <Settings className="w-4 h-4 mr-2" />
            {isCustomizing ? 'Done' : 'Customize'}
          </Button>
        </div>
      </div>

      {isCustomizing && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Customize Layout</h3>
            <Button variant="outline" size="sm" onClick={resetToDefault}>
              Reset to Default
            </Button>
          </div>
          <div className="space-y-2">
            {layout.widgets.map(layoutWidget => {
              const widget = availableWidgets.find(w => w.id === layoutWidget.id);
              if (!widget) return null;

              return (
                <div key={layoutWidget.id} className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-3">
                    <Grip className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{widget.title}</span>
                    <Badge variant="secondary">{widget.size}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={layoutWidget.visible}
                      onCheckedChange={() => toggleWidgetVisibility(layoutWidget.id)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeWidget(layoutWidget.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="dashboard">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6"
            >
              {visibleWidgets.map((layoutWidget, index) => {
                const widget = availableWidgets.find(w => w.id === layoutWidget.id);
                if (!widget) return null;

                return (
                  <Draggable key={layoutWidget.id} draggableId={layoutWidget.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`${getGridClass(widget.size)} ${
                          snapshot.isDragging ? 'opacity-75' : ''
                        }`}
                      >
                        <div className="relative">
                          {isCustomizing && (
                            <div
                              {...provided.dragHandleProps}
                              className="absolute top-2 right-2 z-10 p-1 bg-background border rounded cursor-grab"
                            >
                              <Grip className="w-3 h-3" />
                            </div>
                          )}
                          {getWidgetComponent(layoutWidget.id)}
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};